import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { POST_CONFIGS } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

type MessageBody = {
	body?: string;
};

/**
 * Extracts the message body from a post message.
 * Handles both plain text messages and JSON-formatted messages.
 * @param message - The post message (can be null, plain text, or JSON string)
 * @returns The extracted message text or empty string
 */
function extractMessage(message: string | null): string {
	if (!message) return '';

	try {
		const parsed = JSON.parse(message) as MessageBody;
		if ('body' in parsed) {
			return parsed.body || '';
		}
	} catch {
		// Not JSON, use message as-is
	}

	return message;
}

/**
 * Validates if a story post has the correct variant image based on petition photo status.
 */
function validateStoryContent(
	post: ReadyToSharePostWithPlatform,
	petition: StarterDashboardV2PageData['petition'],
): boolean {
	const hasValidImages = post.images.length > 0 && post.images.some((img) => !img.processing);
	if (!hasValidImages) return false;

	// Get the full post type to find the config
	const fullPostType = `${post.platform}_${post.postTypeName}` as keyof typeof POST_CONFIGS;
	const postConfig = POST_CONFIGS[fullPostType];

	// If no variant specification, fall back to basic validation
	if (!postConfig.shareVisualVariants) {
		return hasValidImages;
	}

	// Determine which variant we expect based on petition photo status
	const petitionHasPhoto = !!petition.photo;
	const expectedVariant = petitionHasPhoto
		? postConfig.shareVisualVariants.variantWithImage
		: postConfig.shareVisualVariants.variantWithoutImage;

	// Check if the expected variant exists in the images
	return post.images.some((img) => img.variationName === expectedVariant && !img.processing);
}

/**
 * Validates if a post has all required content to be displayed.
 * @param post - The Ready to Share post
 * @param isStory - Whether this is a story (true) or regular post (false)
 * @param recommendationsData - Data about recommended groups for platforms that require them
 * @param petition - The petition data (used to determine if petition has photo for story variant validation)
 * @returns true if the post has valid content, false otherwise
 *
 * Validation rules:
 * - Regular posts: Must have BOTH a valid message AND at least one non-processing image
 * - Stories: Must have the correct variant image based on petition photo status
 */
export function hasValidContent(
	post: ReadyToSharePostWithPlatform,
	isStory: boolean,
	recommendationsData: {
		hasFacebookGroups: boolean;
		hasRedditGroups: boolean;
		isLoading: boolean;
	},
	petition: StarterDashboardV2PageData['petition'],
): boolean {
	// Check for valid, non-processing images
	const hasValidImages = post.images.length > 0 && post.images.some((img) => !img.processing);

	const { hasFacebookGroups, hasRedditGroups, isLoading: isRecommendationsLoading } = recommendationsData;

	// Stories need the correct variant based on petition photo status
	if (isStory) {
		return validateStoryContent(post, petition);
	}

	// Regular posts need both message and images
	const message = extractMessage(post.message);
	const hasValidMessage = message.length > 0;

	// group posts require a valid message and at least one recommended group
	if (post.platform === 'reddit') {
		return hasValidMessage && (hasRedditGroups || isRecommendationsLoading);
	}

	if (post.platform === 'facebookgroups') {
		return hasValidMessage && (hasFacebookGroups || isRecommendationsLoading);
	}

	return hasValidMessage && hasValidImages;
}
